import { useEffect } from 'react';

import { openAiPixelId, redditPixelId } from '../config/configAnalytics';
import { configureGoogleAdsTag } from './analytics';
import { CONSENT_CHANGED_EVENT } from './consent';
import { applyOpenAiConsent, initOpenAiPixel } from './openai';
import { initRedditPixel } from './reddit';

const GTAG_SCRIPT_ID = 'deferred-google-gtag-js';
const PLAUSIBLE_SCRIPT_ID = 'deferred-plausible-js';

/**
 * Lädt Analytics-Skripte erst nach `window` **load** und idealerweise in einer **Idle-Phase**
 * (`requestIdleCallback`), um Main-Thread und First Paint zu entlasten.
 * Nur im **Browser** (ClientApp); erscheint nicht im SSR-HTML.
 *
 * GA4: Initial-`page_view` erfolgt nach Laden von `gtag.js` (vgl. `GoogleAnalyticsHandler` für SPA-Navigation).
 *
 * Reddit- und OpenAI-Pixel: gleiche Idle-Phase, aber zusätzlich einwilligungsabhängig — beide
 * kennen keinen Consent Mode, also werden sie im EWR/UK ohne Zustimmung gar nicht erst geladen
 * (`util/reddit.js`, `util/openai.js`). Nach einer nachträglichen Zustimmung im Banner holt der
 * `CONSENT_CHANGED_EVENT`-Listener unten das Laden nach; ein Widerspruch schaltet das bereits
 * geladene OpenAI-SDK ausserdem stumm.
 *
 * @param {Object} props
 * @param {Object} [props.analytics] – `config.analytics` (googleAnalyticsId, plausibleDomains)
 * @returns {null}
 */
const DeferredAnalyticsScripts = props => {
  const { analytics } = props;
  const googleAnalyticsId = analytics?.googleAnalyticsId;
  const plausibleDomains = analytics?.plausibleDomains;
  useEffect(() => {
    const hasGa4 = googleAnalyticsId?.indexOf('G-') === 0;
    const hasPlausible = Boolean(plausibleDomains);
    const hasReddit = Boolean(redditPixelId);
    const hasOpenAi = Boolean(openAiPixelId);
    if (!hasGa4 && !hasPlausible && !hasReddit && !hasOpenAi) {
      return undefined;
    }

    let cancelled = false;

    const injectGoogleAnalytics = () => {
      if (
        cancelled ||
        document.getElementById(GTAG_SCRIPT_ID) ||
        googleAnalyticsId?.indexOf('G-') !== 0
      ) {
        return;
      }
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', googleAnalyticsId, {
        cookie_flags: 'SameSite=None;Secure',
      });
      // Google-Ads-Tag (Remarketing) über dasselbe gtag.js, nur mit REACT_APP_GOOGLE_ADS_ID.
      // Eigene gtag-Gruppe, damit die semantischen Ereignisse nicht zusätzlich in Google Ads
      // landen — Begründung in util/analytics.js.
      configureGoogleAdsTag();

      const s = document.createElement('script');
      s.id = GTAG_SCRIPT_ID;
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`;
      s.crossOrigin = 'anonymous';
      document.head.appendChild(s);
    };

    const injectPlausible = () => {
      if (cancelled || document.getElementById(PLAUSIBLE_SCRIPT_ID) || !hasPlausible) {
        return;
      }
      const s = document.createElement('script');
      s.id = PLAUSIBLE_SCRIPT_ID;
      s.defer = true;
      s.src = 'https://plausible.io/js/script.js';
      s.setAttribute('data-domain', plausibleDomains);
      s.crossOrigin = 'anonymous';
      document.head.appendChild(s);
    };

    // Reddit Ads Pixel. Die Einwilligungs- und Idempotenz-Prüfung steckt in
    // initRedditPixel selbst, deshalb hier nur der cancelled-Guard.
    const injectReddit = () => {
      if (cancelled || !hasReddit) {
        return;
      }
      initRedditPixel();
    };

    // OpenAI Ads Pixel (Werbung in ChatGPT). Einwilligungs- und
    // Idempotenz-Prüfung stecken in initOpenAiPixel selbst.
    const injectOpenAi = () => {
      if (cancelled || !hasOpenAi) {
        return;
      }
      initOpenAiPixel();
    };

    const run = () => {
      if (cancelled) {
        return;
      }
      const schedule =
        typeof window.requestIdleCallback === 'function'
          ? cb => window.requestIdleCallback(cb, { timeout: 4000 })
          : cb => window.setTimeout(cb, 1);
      schedule(() => {
        injectGoogleAnalytics();
        injectPlausible();
        injectReddit();
        injectOpenAi();
      });
    };

    // Zustimmung kann erst nach dem Idle-Lauf erteilt werden (das Banner mountet
    // nach der Hydration). `initRedditPixel` ist idempotent, ein zweiter Aufruf
    // lädt also nichts doppelt. `applyOpenAiConsent` lädt bei Zustimmung nach —
    // und schaltet bei einem Widerspruch ein schon geladenes SDK stumm.
    const onConsentChanged = () => {
      if (!cancelled) {
        initRedditPixel();
        applyOpenAiConsent();
      }
    };
    window.addEventListener(CONSENT_CHANGED_EVENT, onConsentChanged);

    if (document.readyState === 'complete') {
      run();
    } else {
      window.addEventListener('load', run, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('load', run);
      window.removeEventListener(CONSENT_CHANGED_EVENT, onConsentChanged);
    };
  }, [googleAnalyticsId, plausibleDomains]);

  return null;
};

export default DeferredAnalyticsScripts;
